import React, { useCallback, useRef, useState } from "react";
import {
    approveAccrualReviewRequest,
    useApproveWithScheduleResolutions,
} from "@components/accounting/schedules/linkedConflict/useApproveWithScheduleResolutions";
import { hasDocumentsLeftAfterUnmatch } from "@components/accounting/transactionMatch/schedule/matchResolutions";
import { useQueryClient } from "@tanstack/react-query";
import classNames from "classnames";
import {
    AccrualMatchAuditItemType,
    AccrualMatchAuditSnapshot,
} from "@common/schemas/domains/accrualReview/accrual-match-audit-log.schema";
import { undoAccrualReview } from "@api/accrual-review.api";
import { auditLogQueryKeys } from "@api/auditLog.api";
import { invalidateOrganizationHistory } from "@api/organizationAuditLog.api";
import { getInvoiceAccrualMatches } from "@api/invoice.api";
import { getBillAccrualMatches } from "@api/bill.api";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { useScope } from "@state/scopeContext.lib";
import { invalidateAccrualMatchCaches } from "@components/accounting/accrualReview/accrualReviewCache";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { DetachIcon, MatchedIcon, RevertIcon } from "@icons";
import { buildApproveBodyFromSnapshot } from "./buildApproveBodyFromSnapshot";
import styles from "./AccrualMatchHistoryAction.module.scss";

/**
 * Looks up the snapshot's first invoice or bill (if any) and asks the
 * backend whether the source transaction is currently matched to it. Used
 * as a pre-flight check so undo/redo operate only when the displayed
 * snapshot still reflects the current match state — without this, clicking
 * "undo" on an old audit-log entry could remove a different (newer) match
 * on the same transaction.
 *
 * Returns:
 *   - `true`  → source transaction currently matched to the snapshot's doc
 *   - `false` → source transaction is NOT currently matched to that doc
 *   - `null`  → snapshot has no document items (transfer-style match) and
 *               we have no cheap way to check; caller should proceed.
 */
async function isSnapshotCurrent(
    snapshot: AccrualMatchAuditSnapshot,
    workspaceId: string,
): Promise<boolean | null> {
    const invoiceItem = snapshot.items.find(
        (item) => item.type === AccrualMatchAuditItemType.INVOICE_LINE_ITEM,
    );
    const billItem = snapshot.items.find(
        (item) => item.type === AccrualMatchAuditItemType.BILL_LINE_ITEM,
    );

    let currentMatches: Array<{ transactionId: number }> | null = null;
    if (
        invoiceItem &&
        invoiceItem.type === AccrualMatchAuditItemType.INVOICE_LINE_ITEM
    ) {
        currentMatches = await getInvoiceAccrualMatches({
            workspaceId,
            invoiceId: invoiceItem.documentId,
        });
    } else if (
        billItem &&
        billItem.type === AccrualMatchAuditItemType.BILL_LINE_ITEM
    ) {
        currentMatches = await getBillAccrualMatches({
            workspaceId,
            billId: billItem.documentId,
        });
    }
    if (currentMatches === null) {
        return null;
    }

    const sourceTxId = snapshot.transaction.transactionId;
    return currentMatches.some((m) => m.transactionId === sourceTxId);
}

interface Props {
    snapshot: AccrualMatchAuditSnapshot;
    matched: boolean;
    /**
     * `sourceId` of the audit log this card represents. When supplied, the
     * server marks every audit row sharing this id as `revertedAt = NOW()`
     * after the undo/redo succeeds, so the History UI can render the whole
     * batch as reverted.
     */
    sourceId?: string | null;
    /**
     * Already-reverted logs disable the action entirely (the action would
     * be a no-op that creates duplicate matches or unmatches the wrong row).
     */
    isReverted?: boolean;
    /**
     * Invoked after a successful undo/redo (and after audit-log queries are
     * invalidated). Callers use this to decide whether to close the
     * containing drawer based on remaining un-reverted cards rather than
     * dismissing it eagerly.
     */
    onAfterRevert?: () => void;
    className?: string;
    style?: React.CSSProperties;
}

export const AccrualMatchHistoryAction: React.FC<Props> = ({
    snapshot,
    matched,
    sourceId,
    isReverted,
    onAfterRevert,
    className,
    style,
}) => {
    const { workspaceId } = useScope();
    const queryClient = useQueryClient();
    const { error: errorToast } = useToaster();
    const [isLoading, setIsLoading] = useState(false);
    const inFlightRef = useRef(false);

    const { approveWithScheduleResolutions, scheduleResolutionModals } =
        useApproveWithScheduleResolutions();

    const handleClick = useCallback(
        async (e: React.MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            e.preventDefault();
            if (!workspaceId || inFlightRef.current) {
                return;
            }

            const transactionId = snapshot.transaction.transactionId;

            inFlightRef.current = true;
            setIsLoading(true);
            try {
                // Pre-flight stale check: confirm the snapshot still
                // reflects the transaction's current match state. Without
                // this, clicking "undo" on a historical log could remove
                // a different (newer) match on the same transaction.
                const currentlyMatched = await isSnapshotCurrent(
                    snapshot,
                    workspaceId,
                );
                if (matched && currentlyMatched === false) {
                    errorToast(
                        "This match is already undone. Refresh to see the latest activity.",
                    );
                    return;
                }
                if (!matched && currentlyMatched === true) {
                    errorToast(
                        "This transaction is already re-matched. Refresh to see the latest activity.",
                    );
                    return;
                }

                const revertedSourceId = sourceId ?? undefined;
                if (matched) {
                    await undoAccrualReview({
                        workspaceId,
                        transactionId,
                        revertedSourceId,
                    });
                } else {
                    const body = buildApproveBodyFromSnapshot(snapshot);
                    if (!body) {
                        return;
                    }
                    const outcome = await approveWithScheduleResolutions({
                        request: approveAccrualReviewRequest({
                            workspaceId,
                            body: { ...body, revertedSourceId },
                        }),
                        hasWorkLeft: (resolutions) =>
                            hasDocumentsLeftAfterUnmatch({ body, resolutions }),
                    });
                    if (outcome !== "approved") {
                        return;
                    }
                }
                invalidateAccrualMatchCaches(queryClient, { transactionId });
                // Refresh History views so reverted-state styling propagates.
                queryClient.invalidateQueries({
                    queryKey: auditLogQueryKeys.all,
                });
                invalidateOrganizationHistory(queryClient);
                onAfterRevert?.();
            } catch {
                // error toast handled by axios interceptor
            } finally {
                inFlightRef.current = false;
                setIsLoading(false);
            }
        },
        [
            workspaceId,
            snapshot,
            matched,
            sourceId,
            queryClient,
            onAfterRevert,
            errorToast,
            approveWithScheduleResolutions,
        ],
    );

    let tooltip: string;
    if (isReverted) {
        tooltip = "Reverted";
    } else if (matched) {
        tooltip = "Undo match";
    } else {
        tooltip = "Redo match";
    }
    const StatusIcon = matched ? MatchedIcon : DetachIcon;

    return (
        <>
            <Tooltip content={tooltip}>
                <button
                    type="button"
                    className={classNames(styles.action, className)}
                    style={style}
                    onClick={handleClick}
                    disabled={isLoading || !workspaceId || isReverted}
                    aria-label={tooltip}
                >
                    <span className={styles.statusIcon}>
                        <StatusIcon />
                    </span>
                    <span className={styles.hoverIcon}>
                        <RevertIcon />
                    </span>
                </button>
            </Tooltip>
            {scheduleResolutionModals}
        </>
    );
};
